import { Box, Button, TextField } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { PlusIcon, SearchIcon, TrashIcon } from '~/assets/icons';
import { BaseHeaderTitle } from '~/components/common';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import { catchError, handleApiError, notify } from '~/utils/helper';

import useDebounce from '~/hooks/useDebounce';

import usePermission from '~/hooks/usePermission';
import { PERMISSIONS } from '~/utils/constants/permission';

import { jobTypeActions } from '~/thunks/job-type/jobTypeSlice';
import { handleGetJobListTypes, handleGetJobType } from '~/thunks/job-type/jobTypeThunk';
import JobTypeList from '~/components/job-type/JobTypeList/JobTypeList';
import EditJobTypeDialog from '~/components/job-type/EditJobType/EditJobTypeDialog';
import { DIALOG_MODE_CREATE, DIALOG_MODE_EDIT } from '~/utils/constants/common';
import { IJobType, IPayloadDeleteJobType } from '~/utils/interface/jobType';
import jobTypeApi from '~/apis/jobType';
import styles from './JobType.module.scss';
import classNames from 'classnames/bind';
import ModalAlertDelete from '~/components/common/modal-alert-delete/ModalAlertDelete';

const cx = classNames.bind(styles);

const JobType = () => {
  const permissions = usePermission();
  const [searchInput, setSearchInput] = useState<string>('');
  const { t } = useTranslation(['admin/job-type']);

  const dispatch = useAppDispatch();
  const searchInputDebounce = useDebounce<string>(searchInput);
  const { filter, needRefreshData, menuSelectedId, selectedIds, openAlertDelete } = useAppSelector(
    (state) => state.jobType
  );

  const [openEditDialog, setOpenEditDialog] = useState<boolean>(false);
  const [editDialogMode, setEditDialogMode] = useState<string>('');
  const [editData, setEditData] = useState<IJobType>();
  const handleOpen = () => {
    setEditDialogMode(DIALOG_MODE_CREATE);
    setOpenEditDialog(true);
  };

  useEffect(() => {
    if (dispatch && typeof searchInputDebounce !== 'undefined') {
      dispatch(
        jobTypeActions.setFilter({
          search: searchInputDebounce,
          searchFields: ['name', 'code'],
          // page: 1,
        })
      );
    }
  }, [searchInputDebounce, dispatch]);

  useEffect(() => {
    dispatch(handleGetJobListTypes(filter))
      .unwrap()
      .catch((e) => {
        handleApiError(e);
      });
  }, [dispatch, filter, needRefreshData]);

  const handleOpenEdit = async (id: string) => {
    const response = await dispatch(handleGetJobType(id)).unwrap();
    setEditData(response);
    setEditDialogMode(DIALOG_MODE_EDIT);
    setOpenEditDialog(true);

    try {
    } catch (e) {
      handleApiError(e);
    }
  };

  const handleSetOpen = async (open: boolean, _needRefreshData?: boolean) => {
    setOpenEditDialog(open);
  };

  const openAlertDeleteModal = useCallback(() => {
    dispatch(jobTypeActions.changeOpenAlertDelete(true));
  }, [dispatch]);

  const closeAlertDeleteModal = useCallback(() => {
    dispatch(jobTypeActions.changeMenuSelectId(undefined));
    dispatch(jobTypeActions.changeOpenAlertDelete(false));
  }, [dispatch]);

  const onDeleteSelectedJobTypes = useCallback(async () => {
    let payloadDeleteJobTypes: IPayloadDeleteJobType =
      menuSelectedId !== undefined ? { ids: [menuSelectedId] } : { ids: [...selectedIds] };
    try {
      await jobTypeApi.delete(payloadDeleteJobTypes);
      notify('success', t('deleteSuccessMessage'));
      dispatch(jobTypeActions.changeNeedRefreshData(true));
      dispatch(jobTypeActions.changeOpenAlertDelete(false));
      dispatch(jobTypeActions.changeSelectedIds([]));
    } catch (e: unknown) {
      catchError(e, (message) => {
        notify('error', message);
      });
    }
  }, [dispatch, menuSelectedId, selectedIds, t]);

  return (
    <Box className='w-full h-full'>
      <Box className='mt-[20px] flex flex-col min-[1080px]:flex-row item-center'>
        <Box className='flex flex-col min-[1080px]:flex-row min-[1080px]:flex-1 min-[1080px]:justify-between'>
          <BaseHeaderTitle title={t('title')} />
          <Box className='flex flex-row mt-[10px] min-[1080px]:mt-0 gap-[10px]'>
            {permissions[PERMISSIONS.jobType_delete] && (
              <Button
                className={cx(
                  { 'visibility: visible': selectedIds.length > 0 },
                  { 'visibility: hidden': selectedIds.length <= 0 }
                )}
                onClick={openAlertDeleteModal}
                variant='contained'
                color='error'
                disableElevation
                startIcon={<TrashIcon className='w-[17px] h-[17px]' stroke='white' />}
              >
                {t('delete', { ns: 'admin/common' })}
              </Button>
            )}
            <TextField
              className='w-[204px]'
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder={t('searchPlaceholder')}
              InputProps={{ startAdornment: <SearchIcon className='w-[16px]' /> }}
            />
          </Box>
        </Box>

        {permissions[PERMISSIONS.jobType_create] && (
          <Box className='flex flex-row mt-[10px] min-[1080px]:flex-row min-[1080px]:ml-[10px] min-[1080px]:mt-0 justify-between'>
            <Button
              className='h-[36px] px-[14px] py-[9.5px] text-[14px]/[16.8px] font-medium rounded-[8px] ml-[10px]'
              variant='contained'
              disableElevation
              startIcon={<PlusIcon className='w-[10px] h-[10px]' />}
              onClick={handleOpen}
            >
              {t('addNew')}
            </Button>
            <EditJobTypeDialog open={openEditDialog} setOpen={handleSetOpen} mode={editDialogMode} data={editData} />
          </Box>
        )}
      </Box>
      <Box className='mt-[40px]'>
        <JobTypeList onOpenEdit={handleOpenEdit} />
      </Box>
      <ModalAlertDelete
        open={openAlertDelete}
        description={t('alertDescription')}
        onCancel={closeAlertDeleteModal}
        onConfirm={onDeleteSelectedJobTypes}
      />
    </Box>
  );
};

export default JobType;
